import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CSVImport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setPreviewData(null);
      setImportResult(null);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handlePreview = async () => {
    if (!file) {
      alert('Please select a CSV file first');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/import-csv-preview`,
        formData,
        {
          headers: getHeaders(),
          withCredentials: true
        }
      );
      setPreviewData(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to parse CSV file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;

    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/admin/confirm-csv-import`,
        { rows: previewData.rows },
        {
          headers: getHeaders(),
          withCredentials: true
        }
      );
      setImportResult(response.data);
      setPreviewData(null);
      setFile(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to import vehicles');
    } finally {
      setImporting(false);
    }
  };

  const downloadSampleCSV = () => {
    try {
      // Sample data with headers and 2 example rows (1 direct sale, 1 auction)
      const sampleData = [
        ['make', 'model', 'year', 'vin', 'mileage', 'location', 'condition', 'damage_type', 'engine_size', 'body_type', 'transmission', 'drivetrain', 'color', 'listing_type', 'price', 'start_price', 'buy_now_price', 'auction_duration', 'dealer_name', 'image_urls', 'description'],
        ['Toyota', 'Camry', '2022', 'VIN123456789ABCD', '15000', 'Lagos, Nigeria', 'Excellent', '', '2.5L', 'Sedan', 'Automatic', 'FWD', 'Silver', 'direct_sale', '18500.00', '', '', '', 'JAMOH Autos', 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb', 'Well maintained Toyota Camry in excellent condition'],
        ['Honda', 'Accord', '2021', 'VIN987654321XYZA', '22000', 'Lagos, Nigeria', 'Good', 'Minor scratches', '2.0L', 'Sedan', 'Automatic', 'FWD', 'Black', 'auction', '', '12000.00', '18000.00', '72', 'Premium Dealer', 'https://images.unsplash.com/photo-1590362891991-f776e747a588', 'Honda Accord in great condition with minor cosmetic scratches']
      ];

      // Create properly quoted CSV content (quote all fields for safety)
      const csvContent = sampleData.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      // Create blob without BOM
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sample_vehicle_import.csv';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup with slight delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('CSV download triggered successfully');
    } catch (error) {
      console.error('CSV download error:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-xl text-textSecondary">Access Denied</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-white/5 rounded transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="text-4xl font-heading font-bold">CSV Bulk Import</h1>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('CSV Download button clicked!');
              
              try {
                // CSV data
                const rows = [
                  ['make', 'model', 'year', 'vin', 'mileage', 'location', 'condition', 'damage_type', 'engine_size', 'body_type', 'transmission', 'drivetrain', 'color', 'listing_type', 'price', 'start_price', 'buy_now_price', 'auction_duration', 'dealer_name', 'image_urls', 'description'],
                  ['Toyota', 'Camry', '2022', 'VIN123456789ABCD', '15000', 'Lagos, Nigeria', 'Excellent', '', '2.5L', 'Sedan', 'Automatic', 'FWD', 'Silver', 'direct_sale', '18500.00', '', '', '', 'JAMOH Autos', 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb', 'Well maintained Toyota Camry in excellent condition'],
                  ['Honda', 'Accord', '2021', 'VIN987654321XYZA', '22000', 'Lagos, Nigeria', 'Good', 'Minor scratches', '2.0L', 'Sedan', 'Automatic', 'FWD', 'Black', 'auction', '', '12000.00', '18000.00', '72', 'Premium Dealer', 'https://images.unsplash.com/photo-1590362891991-f776e747a588', 'Honda Accord in great condition with minor cosmetic scratches']
                ];
                
                // Build CSV string with proper quoting
                const csvContent = rows.map(row => 
                  row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
                ).join('\n');
                
                console.log('CSV content generated, length:', csvContent.length);
                
                // Use data URI approach (more compatible)
                const dataStr = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
                const downloadAnchor = document.createElement('a');
                downloadAnchor.setAttribute('href', dataStr);
                downloadAnchor.setAttribute('download', 'sample_vehicle_import.csv');
                downloadAnchor.style.display = 'none';
                document.body.appendChild(downloadAnchor);
                downloadAnchor.click();
                document.body.removeChild(downloadAnchor);
                
                console.log('✅ CSV download triggered');
                
              } catch (error) {
                console.error('❌ CSV download error:', error);
                alert('Download failed: ' + error.message);
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
            type="button"
          >
            <Download className="w-4 h-4" />
            <span className="text-sm font-heading">Download Sample CSV</span>
          </button>
        </div>

        {/* Upload Section */}
        {!previewData && !importResult && (
          <div className="glass p-8 rounded-sm mb-6">
            <div className="text-center">
              <Upload className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold mb-2">Upload CSV File</h2>
              <p className="text-textSecondary mb-6">
                Select a CSV file containing vehicle listings to import
              </p>
              
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="inline-block px-6 py-3 bg-primary hover:bg-primary/80 text-white font-heading font-bold rounded cursor-pointer transition-colors"
              >
                Select CSV File
              </label>
              
              {file && (
                <div className="mt-4 flex items-center justify-center space-x-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <span className="text-sm">{file.name}</span>
                </div>
              )}
              
              {file && (
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="mt-4 px-6 py-3 bg-white/10 hover:bg-white/20 font-heading font-bold rounded transition-colors disabled:opacity-50"
                >
                  {loading ? 'Parsing CSV...' : 'Preview & Validate'}
                </button>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <h3 className="text-lg font-heading font-bold mb-4">CSV Format Requirements:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-primary mb-2">Required Fields:</p>
                  <ul className="space-y-1 text-textSecondary">
                    <li>• make, model, year, vin</li>
                    <li>• listing_type (auction or direct_sale)</li>
                    <li>• For auction: start_price</li>
                    <li>• For direct_sale: price</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-primary mb-2">Optional Fields:</p>
                  <ul className="space-y-1 text-textSecondary">
                    <li>• mileage, location, condition</li>
                    <li>• transmission, fuel_type, color</li>
                    <li>• buy_now_price (for auctions)</li>
                    <li>• auction_duration (hours, default: 72)</li>
                    <li>• image_urls (comma-separated)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Section */}
        {previewData && !importResult && (
          <div className="glass p-6 rounded-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold mb-2">Preview & Validation</h2>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{previewData.valid_rows} Valid</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span>{previewData.invalid_rows} Invalid</span>
                  </div>
                  <div className="text-textSecondary">Total: {previewData.total_rows}</div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setPreviewData(null);
                    setFile(null);
                  }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 font-heading rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={importing || previewData.valid_rows === 0}
                  className="px-4 py-2 bg-primary hover:bg-primary/80 text-white font-heading font-bold rounded transition-colors disabled:opacity-50"
                >
                  {importing ? 'Importing...' : `Import ${previewData.valid_rows} Vehicles`}
                </button>
              </div>
            </div>

            {/* Preview Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-2 font-heading">Row</th>
                    <th className="text-left p-2 font-heading">Status</th>
                    <th className="text-left p-2 font-heading">Make</th>
                    <th className="text-left p-2 font-heading">Model</th>
                    <th className="text-left p-2 font-heading">Year</th>
                    <th className="text-left p-2 font-heading">VIN</th>
                    <th className="text-left p-2 font-heading">Type</th>
                    <th className="text-left p-2 font-heading">Price</th>
                    <th className="text-left p-2 font-heading">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map((row, idx) => (
                    <tr key={idx} className={`border-b border-white/5 ${!row.valid ? 'bg-red-500/10' : ''}`}>
                      <td className="p-2">{row.row_number}</td>
                      <td className="p-2">
                        {row.valid ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="p-2">{row.data.make}</td>
                      <td className="p-2">{row.data.model}</td>
                      <td className="p-2">{row.data.year}</td>
                      <td className="p-2 text-xs">{row.data.vin}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          row.data.listing_type === 'auction' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {row.data.listing_type}
                        </span>
                      </td>
                      <td className="p-2">
                        {row.data.listing_type === 'auction' 
                          ? `$${row.data.start_price || 'N/A'}`
                          : `$${row.data.price || 'N/A'}`
                        }
                      </td>
                      <td className="p-2 text-xs text-red-400">
                        {row.errors.length > 0 && (
                          <ul className="list-disc list-inside">
                            {row.errors.map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className="glass p-8 rounded-sm text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Import Complete!</h2>
            <div className="text-lg mb-6">
              <span className="text-green-500 font-bold">{importResult.imported}</span> vehicles imported successfully
              {importResult.failed > 0 && (
                <span className="text-red-400 ml-2">
                  ({importResult.failed} failed)
                </span>
              )}
            </div>
            
            {importResult.failed_details && importResult.failed_details.length > 0 && (
              <div className="mt-4 p-4 bg-red-500/10 rounded text-left text-sm">
                <p className="font-semibold text-red-400 mb-2">Failed Rows:</p>
                <ul className="space-y-1 text-textSecondary">
                  {importResult.failed_details.map((fail, idx) => (
                    <li key={idx}>Row {fail.row}: {fail.error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex justify-center space-x-4 mt-6">
              <button
                onClick={() => {
                  setImportResult(null);
                  setFile(null);
                }}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 font-heading rounded transition-colors"
              >
                Import More
              </button>
              <button
                onClick={() => navigate('/marketplace')}
                className="px-6 py-3 bg-primary hover:bg-primary/80 text-white font-heading font-bold rounded transition-colors"
              >
                View Marketplace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImport;
